import { CheckCheck, Trash2, BellOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { NotificationDto } from '@/services/notificationService';

interface NotificationPanelProps {
  notifications: NotificationDto[];
  isLoading: boolean;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
  onNavigate?: () => void;
}

// Resolves the link target from context data set by the backend (currently only
// populated for CommentMention). Package takes priority over order because the
// detail route addresses packages by their own id (/order/:packageId?type=package).
function getNotificationLink(contextJson?: string): string | null {
  if (!contextJson) return null;

  let context: { publicationId?: number; orderId?: number; packageId?: number };
  try {
    context = JSON.parse(contextJson);
  } catch {
    return null;
  }

  const params = new URLSearchParams({ tab: 'publications' });
  if (context.publicationId) {
    params.set('publicationId', String(context.publicationId));
  }

  if (context.packageId) {
    params.set('type', 'package');
    return `/order/${context.packageId}?${params.toString()}`;
  }
  if (context.orderId) {
    params.set('type', 'order');
    return `/order/${context.orderId}?${params.toString()}`;
  }
  return null;
}

export function NotificationPanel({ notifications, isLoading, onMarkAsRead, onDelete, onNavigate }: NotificationPanelProps) {
  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
        <BellOff className="h-8 w-8 opacity-40" />
        <p className="text-sm">Nenhuma notificação</p>
      </div>
    );
  }

  return (
    <ScrollArea className="max-h-[420px]">
      <div className="divide-y">
        {notifications.map((notification) => {
          const link = getNotificationLink(notification.contextJson);
          const handleNavigate = () => {
            if (!notification.isRead) onMarkAsRead(notification.id);
            onNavigate?.();
          };

          const content = (
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium leading-snug', !notification.isRead && 'font-semibold')}>
                {notification.title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true, locale: ptBR })}
              </p>
            </div>
          );

          return (
          <div
            key={notification.id}
            className={cn(
              'px-4 py-3 group hover:bg-muted/50 transition-colors',
              !notification.isRead && 'bg-primary/5 border-l-2 border-l-primary',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              {link ? (
                <Link to={link} onClick={handleNavigate} className="flex-1 min-w-0">
                  {content}
                </Link>
              ) : (
                content
              )}
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                {!notification.isRead && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onMarkAsRead(notification.id)}
                    title="Marcar como lida"
                  >
                    <CheckCheck className="h-3 w-3" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:text-destructive"
                  onClick={() => onDelete(notification.id)}
                  title="Excluir"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
